import type { ReactNode } from 'react'
import { ActionGlyph } from '../../components/ActionGlyph'
import { TourMark } from '../../components/TourMark'
import type { Navigate } from './fanExperience'

interface PageMastProps {
  backTo?: string
  eyebrow: string
  navigate: Navigate
  title: string
  trailing?: ReactNode
}

export function PageMast({ backTo, eyebrow, navigate, title, trailing }: PageMastProps) {
  return (
    <header className="fan-mast">
      <div className="fan-mast__line">
        {backTo ? (
          <button className="fan-icon-button" onClick={() => navigate(backTo)} type="button">
            <ActionGlyph direction="back" />
            <span className="sr-only">Go back</span>
          </button>
        ) : null}
        <p className="fan-eyebrow">{eyebrow}</p>
        {trailing ? <div className="fan-mast__trailing">{trailing}</div> : null}
      </div>
      <h1 className={`fan-title ${title.length > 12 ? 'fan-title--long' : ''}`.trim()}>{title}</h1>
    </header>
  )
}

interface SectionHeadingProps {
  count?: number
  eyebrow?: string
  id?: string
  title: string
  trailing?: ReactNode
}

export function SectionHeading({ count, eyebrow, id, title, trailing }: SectionHeadingProps) {
  return (
    <div className="fan-section-heading">
      <div>
        {eyebrow ? <p className="fan-eyebrow">{eyebrow}</p> : null}
        <h2 aria-label={typeof count === 'number' ? `${title}: ${count}` : undefined} id={id}>
          {title}
          {typeof count === 'number' ? <sup aria-hidden="true">{count}</sup> : null}
        </h2>
      </div>
      {trailing ? <div className="fan-section-heading__trailing">{trailing}</div> : null}
    </div>
  )
}

interface EmptyStateProps {
  actionLabel?: string
  body: string
  onAction?: () => void
  title: string
}

export function EmptyState({ actionLabel, body, onAction, title }: EmptyStateProps) {
  return (
    <div className="fan-empty" role="status">
      <span aria-hidden="true" className="fan-empty__mark"><TourMark name="signal" /></span>
      <h2>{title}</h2>
      <p>{body}</p>
      {actionLabel && onAction ? (
        <button className="fan-button fan-button--light" onClick={onAction} type="button">
          {actionLabel}
        </button>
      ) : null}
    </div>
  )
}

interface InlineStatusProps {
  children: ReactNode
  tone?: 'neutral' | 'success' | 'warning'
}

export function InlineStatus({ children, tone = 'neutral' }: InlineStatusProps) {
  return (
    <p aria-live="polite" className={`fan-inline-status fan-inline-status--${tone}`} role="status">
      {children}
    </p>
  )
}
